import { useCallback, useEffect, useRef, useState } from 'react';
import { ZODIAC_SIGNS, getSunSign } from '../../lib/astrology';
import { getCachedForecast, type ForecastPeriod } from '../../lib/forecastCache';
import { useAuth } from '../../hooks/useAuth';

// ─────────────────────────────────────────
// ForecastWidget, free daily/weekly/monthly forecast preview by sun sign.
// Shown on /servicos/previsao-personalizada to hook visitors before the
// paid personalized forecast pitch.
// ─────────────────────────────────────────

const PERIODS: { key: ForecastPeriod; label: string; emoji: string }[] = [
  { key: 'today', label: 'Hoje',     emoji: '☀️' },
  { key: 'week',  label: 'Semana',   emoji: '📅' },
  { key: 'month', label: 'Mês',      emoji: '🌙' },
];

export function ForecastWidget() {
  const { profile } = useAuth();

  // Auto-detect sign from logged-in profile
  const inferredSign = profile?.sun_sign
    ? profile.sun_sign
    : profile?.birth_date ? getSunSign(profile.birth_date) : '';

  const [selectedSign, setSelectedSign] = useState<string>(inferredSign || 'aries');
  const [period, setPeriod] = useState<ForecastPeriod>('today');
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Keep memory cache across period switches within the page
  const memo = useRef<Record<string, string>>({});

  const fetchForecast = useCallback(async (sign: string, p: ForecastPeriod) => {
    const key = `${sign}-${p}`;
    if (memo.current[key]) {
      setText(memo.current[key]);
      return;
    }
    setLoading(true);
    try {
      const result = await getCachedForecast(sign, p, 'pt-BR', false);
      const value = result ?? 'Calculando sua análise. Isso leva alguns instantes.';
      memo.current[key] = value;
      setText(value);
    } catch {
      setText('Não foi possível carregar a previsão. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSign) fetchForecast(selectedSign, period);
  }, [selectedSign, period, fetchForecast]);

  const currentSign = ZODIAC_SIGNS.find(s => s.key === selectedSign);

  return (
    <section style={{ padding: '3rem 1.5rem 2rem', maxWidth: '920px', margin: '0 auto' }}>
      <div className="card" style={{
        padding: 'clamp(1.5rem, 4vw, 2.25rem)',
        background: 'linear-gradient(180deg, rgba(245,200,66,0.06), rgba(0,0,0,0))',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-card)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>🔭</div>
          <h2 className="font-display" style={{
            fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
            color: 'var(--text-primary)',
            marginBottom: '0.5rem',
          }}>
            Veja agora a previsão gratuita do seu signo
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-body)', lineHeight: 1.6 }}>
            Esta é a versão genérica, a mesma para todos que compartilham o signo solar. A Previsão Personalizada é feita para o SEU mapa.
          </p>
        </div>

        {/* Period tabs */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
          flexWrap: 'wrap',
        }}>
          {PERIODS.map(p => {
            const isActive = period === p.key;
            return (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                style={{
                  padding: '0.5rem 1.1rem',
                  borderRadius: '999px',
                  border: isActive ? '1px solid var(--petrol)' : '1px solid var(--border-subtle)',
                  background: isActive ? 'rgba(245,200,66,0.15)' : 'transparent',
                  color: isActive ? 'var(--petrol)' : 'var(--text-secondary)',
                  fontSize: '0.9rem',
                  fontWeight: isActive ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 200ms ease',
                }}
                aria-pressed={isActive}
              >
                {p.emoji} {p.label}
              </button>
            );
          })}
        </div>

        {/* Sign picker grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(74px, 1fr))',
          gap: '0.35rem',
          marginBottom: '1.25rem',
        }}>
          {ZODIAC_SIGNS.map(s => {
            const isActive = s.key === selectedSign;
            return (
              <button
                key={s.key}
                onClick={() => setSelectedSign(s.key)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.2rem',
                  padding: '0.5rem 0.25rem',
                  borderRadius: 'var(--radius-md)',
                  border: isActive ? '1px solid var(--petrol)' : '1px solid var(--border-subtle)',
                  background: isActive ? 'rgba(245,200,66,0.12)' : 'var(--bg-elevated)',
                  cursor: 'pointer',
                  transition: 'all 200ms ease',
                  minHeight: 'var(--touch-target)',
                }}
                aria-pressed={isActive}
                aria-label={s.name}
              >
                <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{s.emoji}</span>
                <span style={{
                  fontSize: '0.6rem',
                  color: isActive ? 'var(--petrol)' : 'var(--text-tertiary)',
                  fontWeight: isActive ? 600 : 400,
                  textAlign: 'center',
                }}>
                  {s.name}
                </span>
              </button>
            );
          })}
        </div>

        {/* Forecast result */}
        <div style={{
          padding: '1.25rem 1.5rem',
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
          minHeight: '140px',
        }}>
          {currentSign && (
            <p style={{
              fontSize: 'var(--text-caption)',
              color: 'var(--petrol)',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontWeight: 700,
              marginBottom: '0.5rem',
            }}>
              {currentSign.emoji} {currentSign.name} · {PERIODS.find(p => p.key === period)?.label}
            </p>
          )}
          {loading ? (
            <ForecastSkeleton />
          ) : (
            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.75,
              fontSize: 'var(--text-body)',
              whiteSpace: 'pre-line',
              margin: 0,
            }}>
              {text}
            </p>
          )}
        </div>

        {/* Commercial message */}
        {!loading && text && (
          <div style={{
            marginTop: '1.25rem',
            padding: '1rem 1.25rem',
            background: 'linear-gradient(135deg, rgba(31,78,95,0.15), rgba(192,132,252,0.1))',
            border: '1px solid rgba(31,78,95,0.35)',
            borderRadius: 'var(--radius-md)',
          }}>
            <p style={{
              fontSize: 'var(--text-body)',
              color: 'var(--celeste-soft)',
              fontWeight: 600,
              marginBottom: '0.4rem',
            }}>
              ✦ Isso foi a previsão genérica para {currentSign?.name}.
            </p>
            <p style={{
              fontSize: 'var(--text-caption)',
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              margin: 0,
            }}>
              A sua <strong style={{ color: 'var(--petrol)' }}>Previsão Personalizada</strong> é diferente, calculada para o SEU mapa natal, ela mostra exatamente quais planetas estão ativando a sua vida agora. Outra data, outro destino.
            </p>
          </div>
        )}

        <p style={{
          marginTop: '0.75rem',
          fontSize: 'var(--text-caption)',
          color: 'var(--text-muted)',
          textAlign: 'center',
          fontStyle: 'italic',
        }}>
          ✦ Esta previsão é igual para todos de {currentSign?.name}. A sua previsão personalizada é diferente.
        </p>
      </div>
    </section>
  );
}

// ─── Loading skeleton ────────────────────────────────────────────
function ForecastSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {[1, 2, 3, 4].map(i => (
        <div
          key={i}
          className="shimmer"
          style={{
            height: '14px',
            width: i === 4 ? '70%' : '100%',
            borderRadius: 'var(--radius-sm)',
            background: 'rgba(255,255,255,0.06)',
          }}
        />
      ))}
    </div>
  );
}

// ─── Contrast: free sign forecast vs personalized forecast ──────
export function ForecastContrast() {
  return (
    <section style={{ padding: '3rem 1.5rem', maxWidth: '980px', margin: '0 auto' }}>
      <h2 className="font-display" style={{
        fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
        color: 'var(--text-primary)',
        textAlign: 'center',
        marginBottom: '0.75rem',
      }}>
        Sua previsão personalizada vai muito além disso
      </h2>
      <p style={{
        textAlign: 'center',
        color: 'var(--text-secondary)',
        marginBottom: '2rem',
        lineHeight: 1.65,
        maxWidth: '640px',
        marginLeft: 'auto',
        marginRight: 'auto',
      }}>
        A previsão do signo é genérica. A Previsão Personalizada é calculada para o SEU mapa natal, data, hora e cidade de nascimento exatas.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '1rem',
      }}>
        {/* Free sign forecast */}
        <div className="card" style={{
          padding: '1.5rem',
          border: '1px solid var(--border-subtle)',
          background: 'rgba(255,255,255,0.02)',
          opacity: 0.85,
        }}>
          <p style={{
            fontSize: 'var(--text-caption)',
            color: 'var(--text-muted)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            marginBottom: '0.5rem',
          }}>
            Previsão do signo · Gratuita
          </p>
          <h3 style={{ fontSize: '1.05rem', color: 'var(--text-primary)', marginBottom: '0.75rem', fontWeight: 600 }}>
            Igual para todos do mesmo signo solar
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: 'var(--text-secondary)', lineHeight: 1.75, fontSize: 'var(--text-body)' }}>
            <li>• Baseada só no Sol solar</li>
            <li>• Igual para 1/12 da população mundial</li>
            <li>• Não considera seu mapa natal individual</li>
            <li>• Útil como panorama leve do período</li>
          </ul>
        </div>

        {/* Personalized forecast */}
        <div className="card" style={{
          padding: '1.5rem',
          border: '2px solid var(--petrol)',
          background: 'linear-gradient(180deg, rgba(245,200,66,0.08), rgba(0,0,0,0))',
          position: 'relative',
        }}>
          <div style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--petrol)',
            color: '#000',
            fontSize: '0.7rem',
            padding: '2px 10px',
            borderRadius: '999px',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}>
            ✦ PREVISÃO PERSONALIZADA
          </div>
          <p style={{
            fontSize: 'var(--text-caption)',
            color: 'var(--petrol)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
            marginBottom: '0.5rem',
          }}>
            Para o SEU mapa natal
          </p>
          <h3 style={{ fontSize: '1.05rem', color: 'var(--text-primary)', marginBottom: '0.75rem', fontWeight: 600 }}>
            Única para você
          </h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, color: 'var(--text-secondary)', lineHeight: 1.75, fontSize: 'var(--text-body)' }}>
            <li>✦ Cruza os trânsitos com Sol, Lua e planetas pessoais do SEU mapa</li>
            <li>✦ Mostra qual planeta ativa QUAIS áreas da SUA vida agora</li>
            <li>✦ Previsões Diária, Semanal, Mensal e Anual</li>
            <li>✦ Janelas exatas para agir, pausar ou revisar</li>
            <li>✦ Diferente para você, para o seu vizinho e para o seu melhor amigo de mesmo signo</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

